import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle, XCircle, ChevronRight, ChevronDown, Users01 } from "@untitledui/icons";
import type { LimboMessage } from "@/types/api/email-limbo";
import { participantToFirstName } from "@/lib/format-participant-display";
import { formatEmailListDate } from "@/lib/format-email-list-date";
import { useReviewMessage } from "@/hooks/api/use-email-limbo";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { FileTypeIcon } from "@/components/application/documents/file-type-icon";

interface LimboMessageItemProps {
    message: LimboMessage;
    onOpenEmailModal?: (emailId: string) => void;
}

export const LimboMessageItem = ({ message, onOpenEmailModal: _onOpenEmailModal }: LimboMessageItemProps) => {
    const { t } = useTranslation(["pages:activity"]);
    const [recapOpen, setRecapOpen] = useState(false);
    const [participantsOpen, setParticipantsOpen] = useState(false);
    const { mutate: review, isPending } = useReviewMessage();

    const participantsLabel = [...new Set([message.from_address, ...message.to_addresses].filter(Boolean))].map(participantToFirstName).join(", ") || "—";

    const receivedAt = new Date(message.received_at);
    const dateLabel = formatEmailListDate(receivedAt, t("tradeComms.yesterday", { defaultValue: "Yesterday" }));
    const time = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(receivedAt);
    const date = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(receivedAt);

    const handleApprove = (e: React.MouseEvent) => {
        e.stopPropagation();
        review({ messageId: message.message_id, action: "approve" });
    };

    const handleReject = (e: React.MouseEvent) => {
        e.stopPropagation();
        review({ messageId: message.message_id, action: "reject" });
    };

    // Calculate relevance score percentage and color
    const relevancePercent = Math.round(message.relevance_score * 100);
    const relevanceColor =
        message.relevance_score >= 0.65
            ? "warning"
            : message.relevance_score >= 0.55
                ? "brand"
                : "gray";

    return (
        <>
            <div className="flex items-start gap-3 p-3 transition-colors bg-transparent hover:bg-secondary/20 dark:hover:bg-secondary/30">
                <div className="mt-1">
                    <AlertTriangle className="size-5 text-warning-500" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-primary">{message.subject || "(Nessun oggetto)"}</h4>
                        <Badge type="pill-color" size="sm" color={relevanceColor}>
                            {relevancePercent}% rilevanza
                        </Badge>
                    </div>

                    {/* Collapsible participants (Da, A, Ricevuta) - closed by default */}
                    <div className="mt-1.5">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setParticipantsOpen((prev) => !prev);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setParticipantsOpen((prev) => !prev);
                                }
                            }}
                            className="flex items-center gap-2 w-full text-left text-sm text-secondary min-w-0"
                            role="button"
                            tabIndex={0}
                        >
                            <Users01 className="size-4 shrink-0 text-tertiary" />
                            <span className="truncate flex-1">{participantsLabel}</span>
                            <span className="text-tertiary shrink-0">{dateLabel}</span>
                            {participantsOpen ? (
                                <ChevronDown className="size-4 shrink-0 text-tertiary" />
                            ) : (
                                <ChevronRight className="size-4 shrink-0 text-tertiary" />
                            )}
                        </button>
                        {participantsOpen && (
                            <div className="mt-1 space-y-0.5 text-sm pl-6">
                                <p className="text-secondary">
                                    <span className="text-tertiary">Da:</span> {message.from_address}
                                </p>
                                {message.to_addresses.length > 0 && (
                                    <p className="text-secondary">
                                        <span className="text-tertiary">A:</span> {message.to_addresses.join(", ")}
                                    </p>
                                )}
                                <p className="text-secondary">
                                    <span className="text-tertiary">Ricevuta:</span> {date} alle {time}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Relevance reasons */}
                    {message.relevance_reasons.length > 0 && (
                        <div className="mt-2">
                            <p className="text-xs text-tertiary mb-1">Motivi di incertezza:</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs text-secondary">
                                {message.relevance_reasons.map((reason, idx) => (
                                    <li key={idx}>{reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Body preview - collapsible recap (same pattern as All Inbox) */}
                    {message.body_preview && (
                        <div className="mt-1.5">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRecapOpen((prev) => !prev);
                                }}
                                className="flex items-center gap-1 text-sm text-tertiary hover:text-secondary"
                            >
                                {recapOpen ? (
                                    <ChevronDown className="size-4 shrink-0" />
                                ) : (
                                    <ChevronRight className="size-4 shrink-0" />
                                )}
                                <span>{recapOpen ? "Nascondi recap" : "Mostra recap"}</span>
                            </button>
                            {recapOpen && (
                                <p className="mt-1 text-sm text-secondary italic">
                                    {message.body_preview}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Attachments */}
                    {message.has_attachments && (
                        <div className="mt-2 flex items-center gap-1 text-sm text-secondary">
                            <FileTypeIcon type="unknown" size="sm" />
                            <span>
                                {message.attachment_count} {message.attachment_count === 1 ? "allegato" : "allegati"}
                            </span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            color="primary"
                            iconLeading={CheckCircle}
                            onClick={handleApprove}
                            isLoading={isPending}
                            disabled={isPending}
                        >
                            Approva
                        </Button>
                        <Button
                            size="sm"
                            color="tertiary"
                            iconLeading={XCircle}
                            onClick={handleReject}
                            isLoading={isPending}
                            disabled={isPending}
                        >
                            Rifiuta
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LimboMessageItem;
